import React, { useState, useEffect } from "react";
import { db } from "../../../firebase/config";
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, orderBy, getDocs, Timestamp } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";

const CONCEPTOS_CLIENTE = ['Consulta', 'Estudios diagnósticos', 'Tratamientos', 'Cirugías', 'Combinado'];
const CONCEPTOS_PROVEEDOR = ['Insumos clínicos', 'Honorarios', 'Servicios', 'Impuestos', 'Mantenimiento', 'Compra de máquinas y equipos', 'Disponibilidad de dinero', 'Otros'];
const PERIODICIDADES = [
  { value: 'mensual', label: 'Mensual', meses: 1 },
  { value: 'bimestral', label: 'Bimestral', meses: 2 },
  { value: 'trimestral', label: 'Trimestral', meses: 3 },
  { value: 'cuatrimestral', label: 'Cuatrimestral', meses: 4 },
  { value: 'semestral', label: 'Semestral', meses: 6 },
  { value: 'anual', label: 'Anual', meses: 12 },
];

function calcularFechasCuotas(fechaInicio, periodicidad, cantidadPeriodos, tieneAnticipo) {
  const periodo = PERIODICIDADES.find(p => p.value === periodicidad);
  const meses = periodo?.meses || 1;
  const fechas = [];
  const inicio = new Date(fechaInicio);
  const startIndex = tieneAnticipo ? 1 : 0;
  for (let i = startIndex; i < cantidadPeriodos + startIndex; i++) {
    const fecha = new Date(inicio);
    fecha.setMonth(fecha.getMonth() + i * meses);
    fechas.push(fecha);
  }
  return fechas;
}

function BarraProgreso({ pagado, total, theme }) {
  const pct = total > 0 ? Math.min((pagado / total) * 100, 100) : 0;
  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '11px', color: theme.secondary }}>Progreso de pago</span>
        <span style={{ fontSize: '11px', fontWeight: '600', color: pct >= 100 ? theme.success : theme.accent }}>{pct.toFixed(0)}%</span>
      </div>
      <div style={{ height: '6px', background: theme.border, borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? theme.success : `linear-gradient(90deg, ${theme.accent}, ${theme.accentDark})`, borderRadius: '99px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
        <span style={{ fontSize: '10px', color: theme.secondary }}>Pagado: ${pagado.toLocaleString('es-AR')}</span>
        <span style={{ fontSize: '10px', color: theme.secondary }}>Total: ${total.toLocaleString('es-AR')}</span>
      </div>
    </div>
  );
}

function alertaColor(fecha, theme) {
  if (!fecha) return null;
  const hoy = new Date();
  const diff = (fecha - hoy) / (1000 * 60 * 60 * 24);
  if (diff < 0) return { color: theme.error, bg: theme.errorBg, label: 'Vencido' };
  if (diff <= 5) return { color: theme.warning, bg: theme.warningBg, label: 'Próximo' };
  return null;
}

export default function Financiacion() {
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const [tab, setTab] = useState('clientes');
  const [financiaciones, setFinanciaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalDetalle, setModalDetalle] = useState(null);
  const [pacientesDB, setPacientesDB] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);

  const esRecepcion = userRole === 'recepcion';
  const canCreate = ['gestion_general', 'administracion'].includes(userRole);
  const canEdit = ['gestion_general', 'administracion'].includes(userRole);

  const emptyForm = {
    tipo: 'cliente',
    pacienteNombre: '', pacienteDni: '', pacienteEmail: '',
    proveedorNombre: '', proveedorContacto: '', proveedorEmail: '',
    concepto: '', detalleConcepto: '',
    montoTotal: '', moneda: 'ARS', formaPago: 'Efectivo',
    periodicidad: 'mensual', cantidadPeriodos: 3,
    tieneAnticipo: false, montoAnticipo: '',
    tipoCuota: 'fija',
    cuotasVariables: [],
    fechaInicio: new Date().toISOString().split('T')[0],
    observaciones: '',
  };
  const [form, setForm] = useState(emptyForm);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '640px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', border: `0.5px solid ${theme.border}` };

  useEffect(() => {
    if (!clinicaId) return;
    const tipo = tab === 'clientes' ? 'cliente' : 'proveedor';
    const q = query(collection(db, 'financiaciones'), where('clinicaId', '==', clinicaId), where('tipo', '==', tipo), orderBy('creadoEn', 'desc'));
    const unsub = onSnapshot(q, snap => { setFinanciaciones(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); });
    return unsub;
  }, [clinicaId, tab]);

  useEffect(() => {
    if (!clinicaId) return;
    getDocs(query(collection(db, 'pacientes'), where('clinicaId', '==', clinicaId), orderBy('apellido')))
      .then(snap => setPacientesDB(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [clinicaId]);

  function calcularCuotas() {
    const total = parseFloat(form.montoTotal) || 0;
    const anticipo = form.tieneAnticipo ? (parseFloat(form.montoAnticipo) || 0) : 0;
    const base = total - anticipo;
    const n = parseInt(form.cantidadPeriodos) || 1;
    if (form.tipoCuota === 'fija') return Array(n).fill(parseFloat((base / n).toFixed(2)));
    return form.cuotasVariables.map(v => parseFloat(v) || 0);
  }

  async function crearFinanciacion() {
    const cuotas = calcularCuotas();
    const fechas = calcularFechasCuotas(form.fechaInicio, form.periodicidad, parseInt(form.cantidadPeriodos), form.tieneAnticipo);
    const total = parseFloat(form.montoTotal) || 0;
    const anticipo = form.tieneAnticipo ? (parseFloat(form.montoAnticipo) || 0) : 0;

    const pagos = [];
    if (form.tieneAnticipo && anticipo > 0) {
      pagos.push({ tipo: 'anticipo', monto: anticipo, fecha: new Date(form.fechaInicio), estado: 'pendiente', numero: 0 });
    }
    cuotas.forEach((monto, i) => {
      pagos.push({ tipo: 'cuota', numero: i + 1, monto, fecha: fechas[i] || new Date(), estado: 'pendiente' });
    });

    const nombreEntidad = form.tipo === 'cliente' ? form.pacienteNombre : form.proveedorNombre;
    await addDoc(collection(db, 'financiaciones'), {
      ...form, cuotas, pagos, montoTotal: total, montoAnticipo: anticipo,
      cantidadPeriodos: parseInt(form.cantidadPeriodos),
      estado: 'activa', totalPagado: 0, clinicaId,
      creadoPor: currentUser?.email, creadoEn: new Date(),
    });
    await registrarAccion(currentUser?.email, userRole, 'Financiación', `Nueva financiación ${form.tipo}`, nombreEntidad, clinicaId);
    setModalNuevo(false);
    setForm(emptyForm);
  }
  async function cambiarEstado(fin, nuevoEstado) {
    await updateDoc(doc(db, 'financiaciones', fin.id), { estado: nuevoEstado, [`fecha_${nuevoEstado}`]: new Date() });
    const entidad = fin.tipo === 'cliente' ? fin.pacienteNombre : fin.proveedorNombre;
    await registrarAccion(currentUser?.email, userRole, 'Financiación', `Estado: ${nuevoEstado}`, entidad, clinicaId);
    setModalDetalle(prev => prev ? { ...prev, estado: nuevoEstado } : null);
  }

  async function darIngreso(fin, pagoIdx) {
    const pago = fin.pagos[pagoIdx];
    const nuevoPagos = [...fin.pagos];
    nuevoPagos[pagoIdx] = { ...pago, estado: 'pagado', fechaPago: new Date() };
    const totalPagado = nuevoPagos.filter(p => p.estado === 'pagado').reduce((a, p) => a + p.monto, 0);
    const tipoMovimiento = fin.tipo === 'cliente' ? 'ingreso' : 'egreso';
    const descripcion = pago.tipo === 'anticipo' ? `Anticipo` : `Cuota ${pago.numero}/${fin.cantidadPeriodos}`;
    const entidad = fin.tipo === 'cliente' ? fin.pacienteNombre : fin.proveedorNombre;

    await addDoc(collection(db, 'caja'), {
      tipo: tipoMovimiento, categoria: fin.tipo === 'cliente' ? 'Financiación a Pacientes' : 'Financiación con proveedores',
      detalle: `${descripcion} — ${entidad} · ${fin.concepto}`,
      monto: pago.monto, moneda: fin.moneda, formaPago: fin.formaPago,
      fecha: Timestamp.fromDate(new Date()),
      registradoPor: currentUser?.email, rol: userRole,
      estado: esRecepcion ? 'pendiente_arqueo' : 'confirmado', clinicaId,
    });

    await updateDoc(doc(db, 'financiaciones', fin.id), {
      pagos: nuevoPagos, totalPagado,
      estado: totalPagado >= fin.montoTotal ? 'completada' : 'activa',
    });
    await registrarAccion(currentUser?.email, userRole, 'Financiación', `Pago registrado: ${descripcion}`, entidad, clinicaId);
    setModalDetalle(prev => prev ? { ...prev, pagos: nuevoPagos, totalPagado } : null);
  }

  const lista = financiaciones;

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Financiación" backLabel="Dashboard" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[['clientes', 'Financiación a Pacientes'], ['proveedores', 'Financiación con Proveedores']].map(([v, l]) => (
              (!esRecepcion || v === 'clientes') && (
                <button key={v} onClick={() => setTab(v)} style={{ padding: '8px 16px', fontSize: '13px', borderRadius: '8px', cursor: 'pointer', border: tab === v ? 'none' : `0.5px solid ${theme.borderInput}`, background: tab === v ? theme.accent : 'transparent', color: tab === v ? '#FFFFFF' : theme.secondary, fontFamily: "'Montserrat', sans-serif", fontWeight: tab === v ? '600' : '400' }}>
                  {l}
                </button>
              )
            ))}
          </div>
          {canCreate && (
            <button onClick={() => { setForm({ ...emptyForm, tipo: tab === 'clientes' ? 'cliente' : 'proveedor' }); setModalNuevo(true); }} style={btnPrimaryStyle}>
              + Nueva financiación
            </button>
          )}
        </div>

        {loading
          ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px' }}>Cargando...</div>
          : lista.length === 0
            ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px', fontSize: '13px' }}>No hay financiaciones registradas</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {lista.map(fin => {
                  const entidad = fin.tipo === 'cliente' ? fin.pacienteNombre : fin.proveedorNombre;
                  const pagoPendiente = fin.pagos?.find(p => p.estado === 'pendiente');
                  const fechaPendiente = pagoPendiente?.fecha?.toDate ? pagoPendiente.fecha.toDate() : pagoPendiente?.fecha ? new Date(pagoPendiente.fecha) : null;
                  const alerta = alertaColor(fechaPendiente, theme);
                  return (
                    <div key={fin.id} style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${alerta ? alerta.color : theme.border}`, padding: '16px 20px', cursor: 'pointer' }}
                      onClick={() => setModalDetalle(fin)}
                      onMouseEnter={e => e.currentTarget.style.borderColor = theme.accent}
                      onMouseLeave={e => e.currentTarget.style.borderColor = alerta ? alerta.color : theme.border}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <div style={{ fontSize: '15px', fontWeight: '600', color: theme.primary }}>{entidad}</div>
                          <div style={{ fontSize: '12px', color: theme.secondary }}>{fin.concepto} · {fin.periodicidad} · {fin.cantidadPeriodos} períodos</div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          {alerta && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: alerta.bg, color: alerta.color, fontWeight: '500' }}>{alerta.label}</span>}
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: fin.estado === 'completada' ? theme.successBg : fin.estado === 'inactiva' ? theme.bgCard : theme.infoBg, color: fin.estado === 'completada' ? theme.success : fin.estado === 'inactiva' ? theme.secondary : theme.accent, fontWeight: '500' }}>
                            {fin.estado === 'completada' ? 'Completada' : fin.estado === 'inactiva' ? 'Inactiva' : 'Activa'}
                          </span>
                        </div>
                      </div>
                      <BarraProgreso pagado={fin.totalPagado || 0} total={fin.montoTotal || 0} theme={theme} />
                    </div>
                  );
                })}
              </div>
        }
      </div>

      {modalDetalle && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: '680px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>
                {modalDetalle.tipo === 'cliente' ? modalDetalle.pacienteNombre : modalDetalle.proveedorNombre}
              </span>
              <button onClick={() => setModalDetalle(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '16px' }}>
              {[
                ['CONCEPTO', modalDetalle.concepto],
                ['MONTO TOTAL', `${modalDetalle.moneda === 'USD' ? 'USD ' : '$'}${parseFloat(modalDetalle.montoTotal).toLocaleString('es-AR')}`],
                ['MONEDA', modalDetalle.moneda],
                ['FORMA DE PAGO', modalDetalle.formaPago],
                ['PERIODICIDAD', modalDetalle.periodicidad],
                ['PERÍODOS', modalDetalle.cantidadPeriodos],
              ].map(([label, valor]) => (
                <div key={label} style={{ background: theme.bg, padding: '8px 12px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '10px', color: theme.secondary, marginBottom: '2px', fontWeight: '500' }}>{label}</div>
                  <div style={{ fontSize: '13px', color: theme.primary, fontWeight: '600' }}>{valor}</div>
                </div>
              ))}
            </div>

            {modalDetalle.detalleConcepto && (
              <div style={{ background: theme.bgCard, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', color: theme.secondary, marginBottom: '16px', border: `0.5px solid ${theme.border}` }}>
                {modalDetalle.detalleConcepto}
              </div>
            )}

            <BarraProgreso pagado={modalDetalle.totalPagado || 0} total={modalDetalle.montoTotal || 0} theme={theme} />

            <div style={{ marginTop: '20px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: theme.accent, marginBottom: '12px', letterSpacing: '1px' }}>DETALLE DE PAGOS</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {modalDetalle.pagos?.map((pago, i) => {
                  const fechaPago = pago.fecha?.toDate ? pago.fecha.toDate() : pago.fecha ? new Date(pago.fecha) : null;
                  const alerta = pago.estado === 'pendiente' ? alertaColor(fechaPago, theme) : null;
                  const descripcion = pago.tipo === 'anticipo' ? 'Anticipo' : `Cuota ${pago.numero}/${modalDetalle.cantidadPeriodos}`;
                  return (
                    <div key={i} style={{ background: theme.bgCard, borderRadius: '8px', padding: '10px 14px', border: `0.5px solid ${alerta ? alerta.color : theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{descripcion}</div>
                        <div style={{ fontSize: '11px', color: theme.secondary }}>{fechaPago ? fechaPago.toLocaleDateString('es-AR') : '—'}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {alerta && <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '99px', background: alerta.bg, color: alerta.color, fontWeight: '500' }}>{alerta.label}</span>}
                        <span style={{ fontSize: '14px', fontWeight: '700', color: pago.estado === 'pagado' ? theme.success : theme.primary }}>
                          {modalDetalle.moneda === 'USD' ? 'USD ' : '$'}{parseFloat(pago.monto).toLocaleString('es-AR')}
                        </span>
                        {pago.estado === 'pagado'
                          ? <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: theme.successBg, color: theme.success, fontWeight: '500' }}>Pagado</span>
                          : (canEdit || esRecepcion) && (
                              <button onClick={() => darIngreso(modalDetalle, i)} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, color: '#FFFFFF', border: 'none', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", fontWeight: '600' }}>
                                {modalDetalle.tipo === 'cliente' ? 'Dar ingreso' : 'Dar egreso'}
                              </button>
                            )
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
  {canEdit && modalDetalle.estado === 'activa' && (
    <button onClick={() => cambiarEstado(modalDetalle, 'inactiva')} style={{ ...btnSecondaryStyle, color: theme.error, borderColor: theme.error }}>
      Desactivar financiación
    </button>
  )}
  {canEdit && modalDetalle.estado === 'inactiva' && (
    <button onClick={() => cambiarEstado(modalDetalle, 'activa')} style={{ ...btnSecondaryStyle, color: theme.success, borderColor: theme.success }}>
      Reactivar financiación
    </button>
  )}
  <button onClick={() => setModalDetalle(null)} style={{ ...btnSecondaryStyle, marginLeft: 'auto' }}>Cerrar</button>
</div>
          </div>
        </div>
      )}

      {modalNuevo && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>
                {form.tipo === 'cliente' ? 'Nueva financiación a paciente' : 'Nueva financiación con proveedor'}
              </span>
              <button onClick={() => setModalNuevo(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {form.tipo === 'cliente' ? (
                <div style={{ position: 'relative' }}>
                  <label style={labelStyle}>PACIENTE *</label>
                  <input value={form.pacienteNombre} onChange={e => { const v = e.target.value; setForm({...form, pacienteNombre: v}); if (v.length >= 2) setSugerencias(pacientesDB.filter(p => `${p.apellido} ${p.nombre}`.toLowerCase().includes(v.toLowerCase()) || p.dni?.includes(v)).slice(0,5)); else setSugerencias([]); }} placeholder="Apellido, nombre o DNI..." style={inputStyle} />
                  {sugerencias.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: theme.bgModal, border: `0.5px solid ${theme.border}`, borderRadius: '8px', overflow: 'hidden', marginTop: '2px', boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                      {sugerencias.map(p => (
                        <div key={p.id} onClick={() => { setForm({...form, pacienteNombre: `${p.apellido}, ${p.nombre}`, pacienteDni: p.dni || '', pacienteEmail: p.email || ''}); setSugerencias([]); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `0.5px solid ${theme.border}` }} onMouseEnter={e => e.currentTarget.style.background = theme.bgCard} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{p.apellido}, {p.nombre}</div>
                          <div style={{ fontSize: '11px', color: theme.secondary }}>DNI {p.dni}</div>
                        </div>
                      ))}
                      <div onClick={() => setSugerencias([])} style={{ padding: '8px 14px', fontSize: '12px', color: theme.secondary, cursor: 'pointer', textAlign: 'center' }}>Nombre nuevo</div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>PROVEEDOR *</label><input value={form.proveedorNombre} onChange={e => setForm({...form, proveedorNombre: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>CONTACTO</label><input value={form.proveedorContacto} onChange={e => setForm({...form, proveedorContacto: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>EMAIL</label><input value={form.proveedorEmail} onChange={e => setForm({...form, proveedorEmail: e.target.value})} style={inputStyle} /></div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><label style={labelStyle}>CONCEPTO *</label>
                  <select value={form.concepto} onChange={e => setForm({...form, concepto: e.target.value})} style={inputStyle}>
                    <option value="">Seleccionar...</option>
                    {(form.tipo === 'cliente' ? CONCEPTOS_CLIENTE : CONCEPTOS_PROVEEDOR).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>FECHA DE INICIO</label><input type="date" value={form.fechaInicio} onChange={e => setForm({...form, fechaInicio: e.target.value})} style={inputStyle} /></div>
                <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>DETALLE DEL CONCEPTO</label><input value={form.detalleConcepto} onChange={e => setForm({...form, detalleConcepto: e.target.value})} placeholder="Describí el concepto..." style={inputStyle} /></div>
              </div>

              <div style={{ borderTop: `0.5px solid ${theme.border}`, paddingTop: '14px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: theme.accent, marginBottom: '12px', letterSpacing: '1px' }}>CONDICIONES FINANCIERAS</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div><label style={labelStyle}>MONTO TOTAL *</label><input type="number" value={form.montoTotal} onChange={e => setForm({...form, montoTotal: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>MONEDA</label>
                    <select value={form.moneda} onChange={e => setForm({...form, moneda: e.target.value})} style={inputStyle}>
                      <option value="ARS">ARS — Pesos</option>
                      <option value="USD">USD — Dólares</option>
                    </select>
                  </div>
                  <div><label style={labelStyle}>FORMA DE PAGO</label>
                    <select value={form.formaPago} onChange={e => setForm({...form, formaPago: e.target.value})} style={inputStyle}>
                      <option value="Efectivo">Efectivo</option>
                      <option value="Transferencia">Transferencia</option>
                    </select>
                  </div>
                  <div><label style={labelStyle}>PERIODICIDAD</label>
                    <select value={form.periodicidad} onChange={e => setForm({...form, periodicidad: e.target.value})} style={inputStyle}>
                      {PERIODICIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div><label style={labelStyle}>CANTIDAD DE PERÍODOS</label><input type="number" min="1" value={form.cantidadPeriodos} onChange={e => { const n = parseInt(e.target.value) || 1; setForm({...form, cantidadPeriodos: n, cuotasVariables: Array(n).fill('')}); }} style={inputStyle} /></div>
                  <div><label style={labelStyle}>TIPO DE CUOTA</label>
                    <select value={form.tipoCuota} onChange={e => setForm({...form, tipoCuota: e.target.value})} style={inputStyle}>
                      <option value="fija">Fija</option>
                      <option value="variable">Variable</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                  <input type="checkbox" checked={form.tieneAnticipo} onChange={e => setForm({...form, tieneAnticipo: e.target.checked})} id="anticipo" />
                  <label htmlFor="anticipo" style={{ fontSize: '13px', color: theme.secondary, cursor: 'pointer' }}>Incluir anticipo</label>
                </div>
                {form.tieneAnticipo && (
                  <div style={{ marginTop: '8px' }}>
                    <label style={labelStyle}>MONTO ANTICIPADO</label>
                    <input type="number" value={form.montoAnticipo} onChange={e => setForm({...form, montoAnticipo: e.target.value})} style={inputStyle} />
                  </div>
                )}

                {form.tipoCuota === 'variable' && (
                  <div style={{ marginTop: '12px' }}>
                    <label style={labelStyle}>VALOR DE CADA CUOTA</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px' }}>
                      {Array.from({ length: parseInt(form.cantidadPeriodos) || 1 }, (_, i) => (
                        <div key={i}>
                          <label style={{ ...labelStyle, marginBottom: '4px' }}>Cuota {i + 1}</label>
                          <input type="number" value={form.cuotasVariables[i] || ''} onChange={e => { const cv = [...form.cuotasVariables]; cv[i] = e.target.value; setForm({...form, cuotasVariables: cv}); }} style={inputStyle} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {form.montoTotal && form.cantidadPeriodos && (
                  <div style={{ background: theme.infoBg, padding: '10px 14px', borderRadius: '8px', marginTop: '12px', fontSize: '12px', color: theme.accent }}>
                    {form.tipoCuota === 'fija' ? (
                      <>Cuota fija: <strong>{form.moneda === 'USD' ? 'USD ' : '$'}{((parseFloat(form.montoTotal) - (form.tieneAnticipo ? parseFloat(form.montoAnticipo) || 0 : 0)) / parseInt(form.cantidadPeriodos)).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong> · {form.cantidadPeriodos} períodos {form.periodicidad}s</>
                    ) : (
                      <>Cuotas variables · {form.cantidadPeriodos} períodos {form.periodicidad}s</>
                    )}
                  </div>
                )}
              </div>

              <div><label style={labelStyle}>OBSERVACIONES</label><textarea value={form.observaciones} onChange={e => setForm({...form, observaciones: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={crearFinanciacion} style={btnPrimaryStyle}>Crear financiación</button>
              <button onClick={() => setModalNuevo(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}